import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import cn from 'classnames'
import { Dropdown } from 'antd'
import type { MenuProps } from 'antd'
import { ExportOutlined, LoadingOutlined } from '@ant-design/icons'
import CrmLeadStickerCascadingPicker from '../../../../CrmLeadStickerCascadingPicker/CrmLeadStickerCascadingPicker'
import type { CrmLeadStickerPickPayload } from '../../../../../lib/taskTrackerCrmLeadSticker.menu.utils'
import {
  buildTaskTrackerCrmLeadCardHref,
  openTaskTrackerCrmLeadCardInNewTab,
} from '../../../../../lib/taskTrackerCrmLeadSticker.utils'
import classes from './index.module.css'

const LoadingIcon = LoadingOutlined as unknown as FC<{
  spin?: boolean
  className?: string
}>

const ExternalLinkIcon = ExportOutlined as unknown as FC<{
  className?: string
}>

type TaskStickerCrmLeadValueProps = {
  fieldSettingId: number
  value: string | null
  crmLeadId?: number | null
  crmFunnelId?: number | null
  saving: boolean
  disabled?: boolean
  onSave: (
    fieldSettingId: number,
    value: string | null,
    options?: {
      displayValue?: string | null
      crmLeadId?: number | null
      crmFunnelId?: number | null
    },
  ) => void | Promise<boolean | void>
}

const TaskStickerCrmLeadValue: FC<TaskStickerCrmLeadValueProps> = ({
  fieldSettingId,
  value,
  crmLeadId = null,
  crmFunnelId = null,
  saving,
  disabled = false,
  onSave,
}) => {
  const [pickerOpen, setPickerOpen] = useState(false)
  const [actionsOpen, setActionsOpen] = useState(false)
  const triggerRef = useRef<HTMLSpanElement | null>(null)
  const displayValue = value?.trim() || 'Не выбран'
  const isEmpty = !value?.trim()
  const hasAssignedLead = !isEmpty
  const resolvedLeadId =
    crmLeadId != null && crmLeadId > 0 ? crmLeadId : null
  const resolvedFunnelId =
    crmFunnelId != null && crmFunnelId > 0 ? crmFunnelId : null
  const isInteractionDisabled = disabled || saving

  // trigger={[]} — antd не закрывает по клику снаружи сам.
  useEffect(() => {
    if (!actionsOpen) return

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target
      if (!(target instanceof Node)) {
        setActionsOpen(false)
        return
      }

      if (triggerRef.current?.contains(target)) return
      if (target instanceof Element && target.closest('.ant-dropdown')) {
        return
      }

      setActionsOpen(false)
    }

    document.addEventListener('mousedown', handlePointerDown, true)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown, true)
    }
  }, [actionsOpen])

  const handlePickerOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (isInteractionDisabled) return
      if (nextOpen) {
        setActionsOpen(false)
      }
      setPickerOpen(nextOpen)
    },
    [isInteractionDisabled],
  )

  const handleActionsOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (isInteractionDisabled || !hasAssignedLead) return
      if (nextOpen) {
        setPickerOpen(false)
      }
      setActionsOpen(nextOpen)
    },
    [hasAssignedLead, isInteractionDisabled],
  )

  const handlePick = useCallback(
    (payload: CrmLeadStickerPickPayload) => {
      void (async () => {
        const saved = await onSave(
          fieldSettingId,
          String(payload.leadFunnelId),
          {
            displayValue: payload.leadDisplayName,
            crmLeadId: payload.leadId,
            crmFunnelId: payload.funnelId ?? null,
          },
        )
        if (saved !== false) {
          setPickerOpen(false)
          setActionsOpen(false)
        }
      })()
    },
    [fieldSettingId, onSave],
  )

  const handleClear = useCallback(() => {
    void (async () => {
      const saved = await onSave(fieldSettingId, null, {
        displayValue: null,
        crmLeadId: null,
        crmFunnelId: null,
      })
      if (saved !== false) {
        setPickerOpen(false)
        setActionsOpen(false)
      }
    })()
  }, [fieldSettingId, onSave])

  const actionMenuItems = useMemo<MenuProps['items']>(() => {
    const items: MenuProps['items'] = []

    if (resolvedLeadId != null) {
      items.push({
        key: 'open-lead-card',
        label: 'Перейти в карточку лида',
        onClick: () => {
          setActionsOpen(false)
          openTaskTrackerCrmLeadCardInNewTab(resolvedLeadId, resolvedFunnelId)
        },
      })
    }

    items.push({
      key: 'select-lead',
      label: 'Выбрать лида',
      onClick: () => {
        setActionsOpen(false)
        handlePickerOpenChange(true)
      },
    })

    if (hasAssignedLead) {
      items.push({
        key: 'clear-lead',
        label: 'Очистить',
        danger: true,
        onClick: () => {
          setActionsOpen(false)
          handleClear()
        },
      })
    }

    return items
  }, [
    handleClear,
    handlePickerOpenChange,
    hasAssignedLead,
    resolvedFunnelId,
    resolvedLeadId,
  ])

  const valueControl = (
    <Dropdown
      open={actionsOpen && hasAssignedLead}
      onOpenChange={handleActionsOpenChange}
      trigger={[]}
      menu={{ items: actionMenuItems }}
      getPopupContainer={() => document.body}
    >
      <span ref={triggerRef} className={classes.crmLeadValueTrigger}>
        <CrmLeadStickerCascadingPicker
          open={pickerOpen}
          onOpenChange={handlePickerOpenChange}
          disabled={isInteractionDisabled}
          placement="bottomLeft"
          onPick={handlePick}
        >
          <button
            type="button"
            className={cn(classes.editableValueButton, {
              [classes.editableValueButtonEmpty]: isEmpty,
              [classes.editableValueButtonSaving]: saving,
              [classes.editableValueButtonLocked]: disabled && !saving,
              [classes.editableValueButtonActive]: pickerOpen || actionsOpen,
            })}
            disabled={saving}
            aria-disabled={isInteractionDisabled || undefined}
            aria-expanded={pickerOpen || actionsOpen}
            aria-label={
              hasAssignedLead ? 'Действия со стикером лида' : 'Выбрать лида'
            }
            onClick={() => {
              if (isInteractionDisabled) return

              if (hasAssignedLead) {
                if (pickerOpen) {
                  handlePickerOpenChange(false)
                  return
                }
                handleActionsOpenChange(!actionsOpen)
                return
              }

              handlePickerOpenChange(!pickerOpen)
            }}
          >
            <span className={classes.editableValueText}>{displayValue}</span>
            {saving ? (
              <LoadingIcon spin className={classes.editableValueSavingIcon} />
            ) : null}
          </button>
        </CrmLeadStickerCascadingPicker>
      </span>
    </Dropdown>
  )

  if (resolvedLeadId == null) {
    return valueControl
  }

  return (
    <div className={classes.crmLeadValueRow}>
      {valueControl}
      <a
        href={buildTaskTrackerCrmLeadCardHref(resolvedLeadId, resolvedFunnelId)}
        target="_blank"
        rel="noreferrer noopener"
        className={classes.crmLeadOpenButton}
        aria-label="Перейти в карточку лида"
        title="Перейти в карточку лида"
        onClick={(event) => {
          event.stopPropagation()
        }}
      >
        <ExternalLinkIcon className={classes.crmLeadOpenIcon} />
      </a>
    </div>
  )
}

export default TaskStickerCrmLeadValue
